import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Modal from './Modal';
import Spinner from './Spinner';
import { toast } from './Toast';
import { exportCSV, printTable } from './ExportButton';

const EMPTY = { nrAuto:'', document:'', dataReviziei:'', dataExpirarii:'', kmLaRevizie:'', kmLaUrmRevizie:'', valoare:'', inlocuita:'' };

function zileColor(zile) { return zile<0?'tr-expirat':zile<=30?'tr-expira':''; }
function badge(zile) {
  if (zile<0) return <span className="badge badge-danger">Expirat {Math.abs(zile)}z</span>;
  if (zile<=30) return <span className="badge badge-warning">{zile} zile</span>;
  return <span className="badge badge-ok">{zile} zile</span>;
}
function FilterInput({ value, onChange }) {
  return <input value={value} onChange={e=>onChange(e.target.value)} placeholder="Filtrează..."
    style={{width:'100%',padding:'3px 6px',fontSize:11,border:'1px solid #ccc',borderRadius:3}}/>;
}
function fmt(val) {
  return parseFloat(val||0).toLocaleString('ro-RO', {minimumFractionDigits:2, maximumFractionDigits:2});
}
function esteInlocuit(r) { return parseInt(r.inlocuita) > 0; }

export default function ReviziiTab() {
  const isDriver = (localStorage.getItem('rol') || '') === 'conducator_auto';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nruri, setNruri] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [filters, setFilters] = useState({});
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sort, setSort] = useState({ col:'dataReviziei', dir:'desc' });
  const [selectedRow, setSelectedRow] = useState(null);
  const [pulseId, setPulseId] = useState(null);
  const [pulseKey, setPulseKey] = useState(0);
  const [reinnoireModal, setReinnoireModal] = useState(null);
  const [reinnoireForm, setReinnoireForm] = useState({});
  const [alerte, setAlerte] = useState([]);
  const username = localStorage.getItem('username') || '';

  useEffect(() => { api.get('/api/auto/nruri').then(r=>setNruri(r.data.map(x=>x.nrInmatriculare))); }, []);
  useEffect(() => { load(); }, []);

  const loadAlerte = () => {
    api.get('/api/revizii/expirare').then(r=>setAlerte(r.data)).catch(()=>setAlerte([]));
  };

  const load = () => {
    setLoading(true); setSelectedRow(null); setPulseId(null);
    api.get('/api/revizii').then(r=>setRows(r.data)).finally(()=>setLoading(false));
    loadAlerte();
  };

  const setFilter = (col,val) => setFilters(prev=>({...prev,[col]:val}));
  const toggleSort = (col) => setSort(prev=>({col,dir:prev.col===col&&prev.dir==='asc'?'desc':'asc'}));
  const sortIcon = (col) => sort.col===col?(sort.dir==='asc'?' ▲':' ▼'):' ⇅';

  const filtered = rows.filter(r=>{
    if (dateFrom && r.dataReviziei && r.dataReviziei < dateFrom) return false;
    if (dateTo && r.dataReviziei && r.dataReviziei > dateTo) return false;
    return Object.entries(filters).every(([col,val])=>!val||String(r[col]||'').toLowerCase().includes(val.toLowerCase()));
  });

  const sorted = [...filtered].sort((a,b)=>{
    const av=String(a[sort.col]||''), bv=String(b[sort.col]||'');
    return sort.dir==='asc'?av.localeCompare(bv):bv.localeCompare(av);
  });

  const totalValoare = filtered.reduce((s,r)=>s+parseFloat(r.valoare||0),0);

  // Documente active (fara bifa de inlocuit), pentru selectorul "Inlocuieste documentul"
  const activeDocs = rows.filter(r => !esteInlocuit(r));

  const selectRow = (row) => {
    const isSel = selectedRow?.nrCrt === row.nrCrt;
    if (isSel) { setSelectedRow(null); setPulseId(null); return; }
    setSelectedRow(row);
    // Cauta documentul vechi pe care randul selectat il inlocuieste (daca il inlocuieste)
    const parinte = rows.find(r => parseInt(r.inlocuita) === row.nrCrt);
    setPulseId(parinte ? parinte.nrCrt : null);
    setPulseKey(k => k+1);
  };

  const openAdd = () => {
    // Daca exista o singura masina disponibila (fie o flota cu un singur vehicul, fie
    // un conducator auto caruia i-a fost alocata o singura masina), o completam automat -
    // nu are rost sa ceri o alegere dintr-un dropdown cu o singura optiune.
    setForm(nruri.length === 1 ? { ...EMPTY, nrAuto: nruri[0] } : EMPTY);
    setModal({mode:'add'});
  };
  const openEdit = (row) => { setForm({...row}); setModal({mode:'edit',id:row.nrCrt}); };

  const save = async () => {
    if (modal.mode==='add') {
      if (!form.nrAuto || !form.document || !form.dataReviziei || !form.dataExpirarii ||
          !String(form.kmLaRevizie||'').trim() || !String(form.kmLaUrmRevizie||'').trim() || !String(form.valoare||'').trim()) {
        toast('Completează toate câmpurile.', 'error'); return;
      }
    }
    try {
      const payload = {...form, operare:username};
      if (modal.mode==='add') await api.post('/api/revizii', payload);
      else await api.put(`/api/revizii/${modal.id}`, payload);
      setModal(null); load();
      toast(modal.mode==='add'?'Revizie adăugată!':'Revizie actualizată!');
    } catch(e) { toast('Eroare: '+(e.response?.data?.error||e.message),'error'); }
  };

  const del = async (id) => {
    if (!window.confirm('Ștergi revizia?')) return;
    try { await api.delete(`/api/revizii/${id}`); load(); toast('Revizie ștearsă!'); }
    catch(e) { toast('Eroare la ștergere','error'); }
  };

  const openReinnoire = (row) => {
    setReinnoireForm({ dataReviziei:'', dataExpirarii:'', kmLaRevizie:'', kmLaUrmRevizie:'', valoare:'' });
    setReinnoireModal(row);
  };

  const saveReinnoire = async () => {
    try {
      await api.post(`/api/revizii/${reinnoireModal.nrCrt}/reinnoire`,
        { ...reinnoireForm, operare: username });
      setReinnoireModal(null);
      load();
      toast('Revizie reînnoită cu succes!');
    } catch(e) { toast('Eroare: '+(e.response?.data?.error||e.message),'error'); }
  };

  // Cand aleg un document de inlocuit, blochez si precompletez Nr. Auto / Document
  const onSelectInlocuieste = (id) => {
    if (!id) { setForm({...form, inlocuita:''}); return; }
    const doc = activeDocs.find(d => String(d.nrCrt)===String(id));
    if (doc) setForm({...form, inlocuita:id, nrAuto:doc.nrAuto, document:doc.document});
    else setForm({...form, inlocuita:id});
  };
  const inlocuiesteBlocat = modal?.mode==='add' && !!form.inlocuita;

  const REQUIRED_REVIZIE = new Set(['nrAuto','document','dataReviziei','dataExpirarii','kmLaRevizie','kmLaUrmRevizie','valoare']);
  const f = (field,label,type='text',opts=null,disabled=false) => (
    <div className="form-group"><label>{label}{REQUIRED_REVIZIE.has(field) && <span style={{color:'#e74c3c'}}> *</span>}</label>
      {opts
        ? <>
            <input type="text" list={`opts-${field}`} value={form[field]||''} disabled={disabled}
              onChange={e=>setForm({...form,[field]:e.target.value})}
              placeholder="Alege din listă sau scrie propriul text"
              style={disabled?{background:'#eee',cursor:'not-allowed'}:undefined}/>
            <datalist id={`opts-${field}`}>
              {opts.map(o=><option key={o} value={o}/>)}
            </datalist>
          </>
        : <input type={type} value={form[field]||''} disabled={disabled}
            onChange={e=>setForm({...form,[field]:e.target.value})}
            style={disabled?{background:'#eee',cursor:'not-allowed'}:undefined}/>
      }
    </div>
  );

  const SH = ({col,children}) => (
    <th onClick={()=>toggleSort(col)} style={{cursor:'pointer',userSelect:'none',whiteSpace:'nowrap'}}>
      {children}{sortIcon(col)}
    </th>
  );

  return (
    <div>
      {alerte.length > 0 && (
        <div style={{background:'#fff8e6',border:'1px solid #f0d78c',borderRadius:6,padding:'10px 14px',margin:'10px 0'}}>
          <div style={{fontWeight:'bold',color:'#856404',marginBottom:6,fontSize:13}}>
            ⚠️ Revizii de făcut sau trecute ({alerte.length}) — pe dată (≤60 zile) sau pe kilometraj (≤3000 km)
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:4}}>
            {alerte.map(a => (
              <div key={a.nrCrt} style={{fontSize:12.5,display:'flex',gap:8,flexWrap:'wrap'}}>
                <b>{a.nrAuto}</b>
                <span style={{color:'#555'}}>{a.document}</span>
                <span style={{color:(a.zileRamase<0||a.kmRamasi<0)?'#c0392b':'#856404'}}>{a.detaliu}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="date-filter-bar">
        <label>Perioadă:</label>
        <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)}/>
        <span className="sep">—</span>
        <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)}/>
        {(dateFrom||dateTo) && <button className="btn btn-sm" style={{background:'#eee'}} onClick={()=>{setDateFrom('');setDateTo('');}}>✕</button>}
      </div>

      <div style={{background:'#e8eef5',padding:'8px 14px',borderBottom:'1px solid #d0dcea',display:'flex',gap:16,alignItems:'center',flexWrap:'wrap'}}>
        <span style={{fontSize:12,color:'#555'}}>{filtered.length} înregistrări{Object.values(filters).some(v=>v)||dateFrom||dateTo?' (filtrate)':''}</span>
        <span style={{fontWeight:'bold',color:'#1a3a5c',fontSize:13}}>Total valoare: {fmt(totalValoare)} RON</span>
        <div style={{marginLeft:'auto',display:'flex',gap:6}}>
          <button className="btn btn-sm" style={{background:'#217346',color:'white'}} onClick={()=>exportCSV(sorted,'revizii')}>📊 CSV</button>
          <button className="btn btn-sm" style={{background:'#555',color:'white'}} onClick={()=>printTable('Revizii / Service',sorted)}>🖨️ Print</button>
          {!isDriver && selectedRow && <button className="btn btn-warning btn-sm" onClick={()=>openEdit(selectedRow)}>✏️ Modifică</button>}
          {!isDriver && selectedRow && !esteInlocuit(selectedRow) &&
            <button className="btn btn-sm" style={{background:'#2d6ca2',color:'white'}} onClick={()=>openReinnoire(selectedRow)}>🔄 Reînnoire</button>}
          {!isDriver && selectedRow && <button className="btn btn-danger btn-sm" onClick={()=>del(selectedRow.nrCrt)}>🗑️ Șterge</button>}
          <button className="btn btn-success btn-sm" onClick={openAdd}>+ Adaugă revizie</button>
        </div>
      </div>

      {loading?<Spinner/>:(
        <div style={{overflowX:'auto'}}>
          <table>
            <thead>
              <tr>
                <SH col="nrAuto">Nr. Auto</SH><SH col="document">Document</SH>
                <SH col="dataReviziei">Data revizie</SH><SH col="dataExpirarii">Expiră</SH>
                <th>Zile rămase</th><SH col="kmLaRevizie">KM revizie</SH>
                <SH col="kmLaUrmRevizie">KM urm.</SH>
                <SH col="valoare">Valoare (RON)</SH>
                <th>Efectuată</th><th>Acțiuni</th>
              </tr>
              <tr style={{background:'#f0f4fa'}}>
                <th>
                  <select value={filters.nrAuto||''} onChange={e=>setFilter('nrAuto',e.target.value)}
                    style={{width:'100%',padding:'3px 6px',fontSize:11,border:'1px solid #ccc',borderRadius:3}}>
                    <option value="">Toate</option>
                    {nruri.map(n=><option key={n} value={n}>{n}</option>)}
                  </select>
                </th>
                <th><FilterInput value={filters.document||''} onChange={v=>setFilter('document',v)}/></th>
                <th></th><th></th><th></th>
                <th><FilterInput value={filters.kmLaRevizie||''} onChange={v=>setFilter('kmLaRevizie',v)}/></th>
                <th></th><th></th><th></th><th></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(r=>{
                const isSel = selectedRow?.nrCrt === r.nrCrt;
                const zile=r.dataExpirarii?Math.round((new Date(r.dataExpirarii)-new Date())/86400000):null;
                const doPuls = pulseId === r.nrCrt;
                return (
                  <tr key={r.nrCrt}
                    onClick={()=>selectRow(r)}
                    style={{cursor:'pointer', background:isSel?'#e8f0fe':undefined}}
                    className={r.inlocuita?'':(zile!==null?zileColor(zile):'')}>
                    <td><b>{r.nrAuto}</b></td><td>{r.document}</td>
                    <td>{r.dataReviziei}</td><td>{r.dataExpirarii}</td>
                    <td>
                      <span key={doPuls?`p${pulseKey}`:'np'} className={doPuls?'pulse-inlocuit':''}>
                        {r.inlocuita?<span className="badge badge-ok">✅</span>:(zile!==null?badge(zile):'')}
                      </span>
                    </td>
                    <td style={{textAlign:'right'}}>{r.kmLaRevizie}</td>
                    <td style={{textAlign:'right'}}>{r.kmLaUrmRevizie}</td>
                    <td style={{textAlign:'right',fontVariantNumeric:'tabular-nums'}}>{fmt(r.valoare)}</td>
                    <td>
                      <span key={doPuls?`p2${pulseKey}`:'np2'} className={doPuls?'pulse-inlocuit':''}>
                        {r.inlocuita?'✅':''}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {sorted.length===0&&<tr><td colSpan={10} style={{textAlign:'center',padding:20,color:'#888'}}>Fără date</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <Modal title={modal.mode==='add'?'Adaugă revizie':'Editează revizie'} onClose={()=>setModal(null)} onSave={save}>
          {modal.mode==='add' && (
            <div className="form-row single" style={{marginBottom:8}}>
              <div className="form-group">
                <label>Înlocuiește documentul</label>
                <select value={form.inlocuita||''} onChange={e=>onSelectInlocuieste(e.target.value)}>
                  <option value="">-- Nu înlocuiește nimic --</option>
                  {activeDocs.map(d=>(
                    <option key={d.nrCrt} value={d.nrCrt}>
                      {d.nrAuto} — {d.document} — expiră: {d.dataExpirarii}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          <div className="form-row">
            {f('nrAuto','Nr. Auto','text',nruri,inlocuiesteBlocat)}
            {f('document','Tip document/revizie','text',['ITP','Revizie periodica','Schimb ulei','Filtru aer','Filtru combustibil','Altele'],inlocuiesteBlocat)}
          </div>
          <div className="form-row">{f('dataReviziei','Data reviziei','date')}{f('dataExpirarii','Data expirare','date')}</div>
          <div className="form-row">{f('kmLaRevizie','KM la revizie','number')}{f('kmLaUrmRevizie','KM urm. revizie','number')}</div>
          <div className="form-row">{f('valoare','Valoare (RON)','number')}
            {modal.mode==='edit' &&
              <div className="form-group"><label>Efectuată</label>
                <select value={form.inlocuita||0} onChange={e=>setForm({...form,inlocuita:e.target.value})}>
                  <option value={0}>Nu</option><option value={1}>Da</option>
                </select>
              </div>}
          </div>
          <div className="form-row single"><span style={{fontSize:11,color:'#888'}}>* Câmp obligatoriu</span></div>
        </Modal>
      )}
      {reinnoireModal && (
        <Modal title={`Reînnoire — ${reinnoireModal.document} (${reinnoireModal.nrAuto})`}
          onClose={()=>setReinnoireModal(null)} onSave={saveReinnoire} saveLabel="Reînnoiește">

          <div style={{background:'#f0f4fa',padding:'8px 12px',borderRadius:4,marginBottom:12,fontSize:12,color:'#555'}}>
            Documentul curent (din {reinnoireModal.dataReviziei}, expiră {reinnoireModal.dataExpirarii}) va fi marcat ca înlocuit.
            Completează datele noii revizii de mai jos.
          </div>

          <div className="form-row">
            <div className="form-group"><label>Nr. Auto</label>
              <input type="text" value={reinnoireModal.nrAuto} disabled/>
            </div>
            <div className="form-group"><label>Tip document/revizie</label>
              <input type="text" value={reinnoireModal.document} disabled/>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group"><label>Data reviziei noi<span style={{color:'#e74c3c'}}> *</span></label>
              <input type="date" value={reinnoireForm.dataReviziei||''} onChange={e=>setReinnoireForm({...reinnoireForm,dataReviziei:e.target.value})}/>
            </div>
            <div className="form-group"><label>Data expirare<span style={{color:'#e74c3c'}}> *</span></label>
              <input type="date" value={reinnoireForm.dataExpirarii||''} onChange={e=>setReinnoireForm({...reinnoireForm,dataExpirarii:e.target.value})}/>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group"><label>KM la revizie<span style={{color:'#e74c3c'}}> *</span></label>
              <input type="number" value={reinnoireForm.kmLaRevizie||''} onChange={e=>setReinnoireForm({...reinnoireForm,kmLaRevizie:e.target.value})}/>
            </div>
            <div className="form-group"><label>KM urm. revizie<span style={{color:'#e74c3c'}}> *</span></label>
              <input type="number" value={reinnoireForm.kmLaUrmRevizie||''} onChange={e=>setReinnoireForm({...reinnoireForm,kmLaUrmRevizie:e.target.value})}/>
            </div>
          </div>

          <div className="form-row single">
            <div className="form-group"><label>Valoare (RON)<span style={{color:'#e74c3c'}}> *</span></label>
              <input type="number" value={reinnoireForm.valoare||''} onChange={e=>setReinnoireForm({...reinnoireForm,valoare:e.target.value})}/>
            </div>
          </div>
          <div className="form-row single"><span style={{fontSize:11,color:'#888'}}>* Câmp obligatoriu</span></div>
        </Modal>
      )}
    </div>
  );
}
